import styled from "@emotion/styled";
import { useTranslation } from "react-i18next";
import { AccountTypeEnum } from "@rentablo/api/apiv1";
import { useMandatorContext } from "@rentablo/theming";
import { AccountsContainer } from "@components";
import { AccountPerformance } from "@components/Accounts/AccountView/hooks/useAccountsPerformance";
import { FilterValue } from "@components/filters";
import { ImportType } from "@features/add/components/types";
import { AccountsGroup, ExtendedAccount } from "./types";

const PageAccountsGridBase = styled.div(({ theme }) => ({
  display: "grid",
  columnGap: theme.spacing(3),
  rowGap: theme.spacing(6),
  marginTop: theme.spacing(6),
  [theme.breakpoints.down("sm")]: {
    columnGap: theme.spacing(1),
  },
}));

const KpmgPageAccountsGrid = styled(PageAccountsGridBase)(({ theme }) => ({
  gridTemplateColumns: "auto minmax(0, 1fr) minmax(0, 1fr) auto auto",
  [theme.breakpoints.down("md")]: {
    gridTemplateColumns: "auto minmax(0, 1fr) auto",
  },
}));

const DefaultPageAccountsGrid = styled(PageAccountsGridBase)(({ theme }) => ({
  gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr) minmax(0, 0.5fr)",
  [theme.breakpoints.down("xl")]: {
    gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr) auto",
  },
  [theme.breakpoints.down("md")]: {
    gridTemplateColumns: "minmax(0, 1fr) auto",
  },
}));

const groupTitleMapper: { [key in AccountTypeEnum]: string } = {
  "01_depot": "depotPage.subcategories.depots",
  "02_cash": "depotPage.subcategories.cash",
  "04_insurance": "depotPage.subcategories.insurance",
  "05_material_assets": "depotPage.subcategories.assets",
};

interface Props {
  groupedAccounts: AccountsGroup[];
  groupsToShow: FilterValue[];
  refMapByDepotId: Partial<Record<number, ExtendedAccount[]>>;
  isAccountsLoading: boolean;
  filter: string;
  onSelect: (val: ImportType) => void;
  performanceById: Record<number, AccountPerformance>;
  performanceValues: Record<number, number>;
}

export const AccountsListGrid = ({
  groupedAccounts,
  groupsToShow,
  refMapByDepotId,
  isAccountsLoading,
  filter,
  onSelect,
  performanceById,
  performanceValues,
}: Props) => {
  const { t } = useTranslation();
  const { isKpmg } = useMandatorContext();
  const Grid = isKpmg ? KpmgPageAccountsGrid : DefaultPageAccountsGrid;

  const visibleGroups = groupedAccounts.filter(({ title }) =>
    groupsToShow.includes(title)
  );

  return (
    <Grid>
      {visibleGroups.map(({ title, accounts }, index) => {
        const importType =
          title === "01_depot" || title === "02_cash" ? "" : "manually";

        return (
          <AccountsContainer
            key={title}
            type={title}
            title={t(groupTitleMapper[title])}
            accounts={accounts}
            refAccountsByDepotId={title === "01_depot" ? refMapByDepotId : {}}
            onAdd={() => onSelect({ type: importType, default: title })}
            isFetching={isAccountsLoading}
            filter={filter}
            isLastGroup={index === visibleGroups.length - 1}
            performanceById={performanceById}
            performanceValues={performanceValues}
          />
        );
      })}
    </Grid>
  );
};
